import React from 'react';
import { 
  Box, 
  Grid, 
  LinearProgress, 
  Typography 
} from '@mui/material';

const MetricItem = ({ label, value, color }) => {
  const numericValue = typeof value === 'string' 
    ? parseFloat(value.replace('%', '')) 
    : value || 0;
  
  // Determine severity level based on value
  let severity = 'success';
  if (numericValue >= 90) {
    severity = 'error';
  } else if (numericValue >= 70) {
    severity = 'warning';
  } else if (numericValue >= 50) {
    severity = 'info';
  }
  
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight="bold">
          {typeof value === 'string' ? value : `${value}%`}
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={Math.min(numericValue, 100)} 
        color={color || severity}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
};

const SystemHealthPanel = ({ metrics = {} }) => {
  const { 
    cpu_usage = '0%',
    memory_usage = '0%', 
    disk_usage = '0%',
    network_latency = '0ms',
    uptime = '0d 0h 0m',
    last_refresh = 'Never'
  } = metrics;
  
  // Convert string percentages to numbers for comparison
  const cpuValue = parseFloat(cpu_usage.replace('%', ''));
  const memoryValue = parseFloat(memory_usage.replace('%', ''));
  const diskValue = parseFloat(disk_usage.replace('%', ''));
  
  // Calculate overall health score
  const overallHealth = Math.max(0, 100 - ((cpuValue + memoryValue + diskValue) / 3));
  
  // Determine overall health status
  let healthStatus = 'Good';
  let healthColor = 'success';
  
  if (overallHealth < 40) {
    healthStatus = 'Critical';
    healthColor = 'error';
  } else if (overallHealth < 70) {
    healthStatus = 'Warning';
    healthColor = 'warning';
  }
  
  return (
    <Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item>
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%',
              backgroundColor: `${healthColor}.main`,
              boxShadow: theme => `0 0 8px ${theme.palette[healthColor].main}`
            }} 
          />
        </Grid>
        <Grid item>
          <Typography variant="h6" color={healthColor}>
            System Health: {healthStatus}
          </Typography>
        </Grid>
      </Grid>
      
      <MetricItem label="CPU Usage" value={cpu_usage} />
      <MetricItem label="Memory Usage" value={memory_usage} />
      <MetricItem label="Disk Usage" value={disk_usage} />
      
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Network Latency
            </Typography>
            <Typography variant="body1">
              {network_latency}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Uptime
            </Typography>
            <Typography variant="body1">
              {uptime}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {last_refresh || new Date().toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default SystemHealthPanel;